import React, { useEffect, useState } from 'react';
import FacebookFeedFallback from '../FacebookFeedFallback';

interface PluginProps {
  onLoad: () => void;
  onError: (error: any) => void;
}

const FacebookPagePlugin = ({ onLoad, onError }: PluginProps) => {
  const [isMobile, setIsMobile] = useState(false);
  const [sdkFailed, setSdkFailed] = useState(false);
  const [isRendered, setIsRendered] = useState(false);

  // Detect mobile devices
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', checkMobile);
    checkMobile();

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Check if Facebook SDK is loaded and working
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Set up a listener for FB SDK initialization
    const checkFbSdk = () => {
      if (window.FB) {
        try {
          // FB SDK is loaded, try to parse XFBML
          window.FB.Event.subscribe('xfbml.render', () => {
            console.log('Facebook XFBML rendered successfully');
            setIsRendered(true);
            onLoad();

            // Fix iframe height after rendering
            setTimeout(() => {
              const fbIframe = document.querySelector('.fb-page iframe');
              if (fbIframe && isMobile) {
                (fbIframe as HTMLIFrameElement).style.minHeight = '450px';
                (fbIframe as HTMLIFrameElement).style.height = '70vh';
              }
            }, 1000);
          });

          window.FB.Event.subscribe('xfbml.error', (error: any) => {
            console.error('Facebook XFBML error:', error);
            setSdkFailed(true);
            onError(error);
          });

          // Parse XFBML to render the plugin
          window.FB.XFBML.parse();
          return true;
        } catch (error) {
          console.error('Error initializing Facebook plugin:', error);
          setSdkFailed(true);
          onError(error);
          return false;
        }
      }
      return false;
    };

    // Try immediately if FB is already loaded
    if (!checkFbSdk()) {
      // If not loaded yet, set up a timeout to check again
      const timeoutId = setTimeout(() => {
        if (!checkFbSdk()) {
          console.error('Facebook SDK not available after timeout');
          setSdkFailed(true);
          onError(new Error('Facebook SDK not available'));
        }
      }, 5000);

      return () => clearTimeout(timeoutId);
    }
  }, [onLoad, onError, isMobile]);

  // Show fallback on mobile if SDK failed
  if (isMobile && sdkFailed) {
    return <FacebookFeedFallback />;
  }

  // Determine appropriate height for the container
  const containerStyle = {
    minHeight: isMobile ? '450px' : 'auto',
    height: isMobile ? '70vh' : '100%',
  };

  return (
    <div
      className={`fb-page-container ${isRendered ? 'rendered' : 'loading'}`}
      style={containerStyle}
    >
      <div
        className="fb-page"
        data-href="https://www.facebook.com/mbautocaregarage"
        data-tabs="timeline"
        data-width={isMobile ? window.innerWidth - 40 : 300}
        data-height={isMobile ? 450 : 450}
        data-small-header={isMobile ? 'true' : 'false'}
        data-adapt-container-width="true"
        data-hide-cover="false"
        data-show-facepile="true"
      >
        <blockquote
          cite="https://www.facebook.com/mbautocaregarage"
          className="fb-xfbml-parse-ignore"
        >
          <a href="https://www.facebook.com/mbautocaregarage">
            MB Auto Care Garage
          </a>
        </blockquote>
      </div>
    </div>
  );
};

export default FacebookPagePlugin;
